import React from 'react';
import { ArrowUpRight, ShieldCheck, Heart, Sparkles } from 'lucide-react';
import { CANONICAL_REVIEW_NOTE } from '../constants';

export default function Footer() {
  return (
    <footer role="contentinfo" className="bg-slate-950 text-white border-t border-slate-800 pt-20 pb-12 px-6">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Canonical Review Note */}
        <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400 leading-relaxed">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-bold text-slate-300 mr-1.5 font-mono text-[11px] uppercase">
                Canonical Release & Verification Policy:
              </span>
              {CANONICAL_REVIEW_NOTE}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Col 1: Identity */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-serif font-bold text-lg">
                m
              </div>
              <span className="text-2xl font-serif font-bold tracking-tight text-white">meUus</span>
            </div>
            <p className="text-xs text-emerald-400 font-mono">
              Start with me · Grow with U · Build us
            </p>
            <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
              meUus is a human-centred development, connection and action ecosystem. We help individuals understand reality, reflect with clarity, connect their capacities, and build sustainable resilience together.
            </p>
            <div className="text-[11px] font-mono text-slate-500 pt-2">
              Founder: Md. Mahady Hasan (Rujbel) · Claim ≤ Evidence
            </div>
          </div>

          {/* Col 2: The 4 Canonical Domains */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-widest text-slate-300 font-mono mb-4">
              Four Domains
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400" aria-label="Four Domains">
              <li>
                <a href="https://meuus.org" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 flex items-center gap-1">
                  <span>meuus.org (Understand)</span>
                  <ArrowUpRight className="w-3 h-3 text-slate-600" />
                </a>
              </li>
              <li>
                <a href="https://meuussoul.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 flex items-center gap-1">
                  <span>meuussoul.com (Learn)</span>
                  <ArrowUpRight className="w-3 h-3 text-slate-600" />
                </a>
              </li>
              <li>
                <a href="https://meuus.app" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 flex items-center gap-1">
                  <span>meuus.app (Reflect & Act)</span>
                  <ArrowUpRight className="w-3 h-3 text-slate-600" />
                </a>
              </li>
              <li>
                <a href="https://rujbel.org" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 flex items-center gap-1">
                  <span>rujbel.org (Founder Archive)</span>
                  <ArrowUpRight className="w-3 h-3 text-slate-600" />
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3: Core Starting Portals */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-widest text-slate-300 font-mono mb-4">
              Starting Portals
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400" aria-label="Starting Portals">
              <li><a href="#start" className="hover:text-emerald-400">Universal Start Door</a></li>
              <li><a href="#dashboard" className="hover:text-emerald-400">90-Day Journey Dashboard</a></li>
              <li><a href="#soul" className="hover:text-emerald-400">Master Soul Profile</a></li>
              <li><a href="#dlas" className="hover:text-emerald-400">DLAS Reflection System</a></li>
              <li><a href="#knowledge" className="hover:text-emerald-400">Knowledge Hub & Library</a></li>
              <li><a href="#marketplace" className="hover:text-emerald-400">Two-Way Marketplace</a></li>
            </ul>
          </div>

          {/* Col 4: Governance & Evidence */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-widest text-slate-300 font-mono mb-4">
              Governance & Truth
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-400" aria-label="Governance & Truth">
              <li><a href="#evidence" className="hover:text-emerald-400">Evidence & Claims Ledger</a></li>
              <li><a href="#safety" className="hover:text-emerald-400">Safety & Amanah Charter</a></li>
              <li><a href="#accessibility" className="hover:text-emerald-400">Accessibility Statement</a></li>
              <li><a href="#about" className="hover:text-emerald-400">Ecosystem History</a></li>
              <li><a href="#appreciation" className="hover:text-emerald-400">Appreciation & Contributors</a></li>
            </ul>
          </div>

          {/* Col 5: Emergency 24/7 Hotlines */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-widest text-rose-400 font-mono mb-4 flex items-center gap-1.5">
              <span>Crisis Hotlines</span>
            </h4>
            <ul className="space-y-2 text-xs text-slate-400" aria-label="Crisis Hotlines">
              <li>
                <a href="tel:999" className="hover:text-rose-300 flex justify-between font-mono">
                  <span>National 999:</span>
                  <span className="text-rose-400 font-bold">999</span>
                </a>
              </li>
              <li>
                <a href="tel:16430" className="hover:text-purple-300 flex justify-between font-mono">
                  <span>Legal Aid:</span>
                  <span className="text-purple-400 font-bold">16430</span>
                </a>
              </li>
              <li>
                <a href="tel:109" className="hover:text-pink-300 flex justify-between font-mono">
                  <span>Women/Child:</span>
                  <span className="text-pink-400 font-bold">109</span>
                </a>
              </li>
              <li>
                <a href="tel:01779554391" className="hover:text-emerald-300 flex justify-between font-mono">
                  <span>Kaan Pete Roi:</span>
                  <span className="text-emerald-400 font-bold">Mental Health</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-mono">
          <p>© 2026 meUus Ecosystem. All Rights Preserved under Claim ≤ Evidence.</p>
          <div className="flex gap-4">
            <span>Consistent, Honest Action Over Perfection</span>
            <span>·</span>
            <span>Privacy by Default</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
